import { useState, useCallback } from 'react';
import { Will } from '../services/api';
import { searchWills } from '../services/api';
import { useDebounce } from './useDebounce';

export const useWillSearch = (initialTerm: string = '') => {
  const [searchTerm, setSearchTerm] = useState(initialTerm);
  const [results, setResults] = useState<Will[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const performSearch = useCallback(async (term: string) => {
    setIsTyping(false);
    if (term.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await searchWills(term);
      setResults(response.content);
      if (response.content.length > 0) {
        setCursor(response.content[response.content.length - 1].createdAt);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useDebounce(performSearch, 300);

  const loadMore = useCallback(async () => {
    if (!cursor || isLoading || searchTerm.length < 2) return;

    try {
      const response = await searchWills(searchTerm, cursor);
      setResults(prev => [...prev, ...response.content]);
      if (response.content.length > 0) {
        setCursor(response.content[response.content.length - 1].createdAt);
      }
    } catch (error) {
      console.error('Load more failed:', error);
    }
  }, [cursor, isLoading, searchTerm]);

  return {
    searchTerm,
    results,
    isLoading,
    isTyping,
    setSearchTerm: (term: string) => {
      setIsTyping(true);
      setSearchTerm(term);
      debouncedSearch(term);
    },
    loadMore,
  };
}; 